import { useDispatch } from 'react-redux';
import { onChangePolicyCoveragePremiumDetails, PolicyCKEditorCoveragePremiumDetailsType, policyCoveragePremiumDetailsType, setPolicyCoveragePremiumDetailsType } from '../../../../../features/ebam/plicy-data-page/policy-coverage-premium-details/policy-coverage-premium-details.slice';
import PolicyCKEditorTextModal from '../../modal/policy-ck-editor-text-modal';
import { useProposalTypeData } from '../../../../../hooks/proposal/use-proposal-type-data';
import PolicySchedulePremiums from './policy-schedule-premiums';
import PolicySchedulePremiumsRates from './policy-schedule-premium-rates';
import PolicyScheduleTerminationAge from './policy-schedule-termination-age';
import PolicyTabHeader from './policy-tab-header';
import { usePolicyActions } from '../../../../../hooks/actions/use-policy-actions';
import CKEditor from '../../../../../library/ckeditor/ck-editor';
import { usePolicyDataState } from '../../../../../hooks/policy/use-policy-data-state';

export default function PolicySchedule() {

    const dispatch = useDispatch()
    const { 
        handleEditScheduleOfInsurance, 
        isLoadingUpdatePolicy, 
        handleCancelEditPolicySchedule 
    } = usePolicyActions()
    const {
        PSAmountInsurance,
        PSCoveragePeriod,
        PSPremiums,
        PSPremiumRates,
        PSRefundPremiums,
        PSTerminationAge,
        isGCLIPolicy,
        isGTLIPolicy,
    } = useProposalTypeData()
    const {
        isPCPAmountOfInsurance,
        isPCPCoveragePeriod,
        isPCPRefundOfPremiums,
        policyCoveragePremium,
        policyAmountInsurance,
        policyCoveragePeriod,
        policyRefundOfPremiums,
    } = usePolicyDataState()

    const handleCKEditorSave = async () => {
        switch (policyCoveragePremium) {
            case "amountOfInsurance":
                await handleEditScheduleOfInsurance('amountOfInsurance');
                break;

            case "coveragePeriod":
                await handleEditScheduleOfInsurance('coveragePeriod');
                break;
            
            case "refundOfPremiums":
                await handleEditScheduleOfInsurance('refundOfPremiums');
                break;

            default:
                break;
        }
    };

    const isCKEditorType = ( type: policyCoveragePremiumDetailsType ): type is PolicyCKEditorCoveragePremiumDetailsType => {
        return (
            type === "amountOfInsurance" ||
            type === "coveragePeriod" ||
            type === "refundOfPremiums"
        );
    };

    const getPolicyInformationTitle = ( type: PolicyCKEditorCoveragePremiumDetailsType ): string => {
        switch (type) {
            case "amountOfInsurance":
                return "for amount of insurance";
            case "coveragePeriod":
                return "for coverage period";
            case "refundOfPremiums":
                return "for refund of premiums";
            default:
                return "";
        }
    };

    const getPolicyInformationValue = ( type: PolicyCKEditorCoveragePremiumDetailsType ): string => {
        switch (type) {
            case "amountOfInsurance":
                return policyAmountInsurance ?? "";
            case "coveragePeriod":
                return policyCoveragePeriod ?? "";
            case "refundOfPremiums":
                return policyRefundOfPremiums ?? "";
            default:
                return "";
        }
    };

    const onChangeCKEditor = (value: string) => {
            if (!isCKEditorType(policyCoveragePremium)) {
                return;
            }
            dispatch(onChangePolicyCoveragePremiumDetails({field: policyCoveragePremium, value}));
        }

    return (
        <section className="overflow-hidden rounded border border-gray-200 bg-white">
            <PolicyTabHeader
                title='Schedule of Insurance'
                subTitle='Enter and manage the applicable insurance coverage details.'
            />
            <div className="py-3 px-4 space-y-4 lg:space-y-5">
                {PSAmountInsurance && (
                    <PolicyCKEditorTextModal
                        title='Amount of Insurance'
                        description='Enter the applicable amount of insurance wording.'
                        isEditing={isPCPAmountOfInsurance}
                        onEdit={() => dispatch(setPolicyCoveragePremiumDetailsType("amountOfInsurance"))}
                        onCancel={() => dispatch(setPolicyCoveragePremiumDetailsType(null))}
                        onSave={() => dispatch(setPolicyCoveragePremiumDetailsType(null))}
                        value={policyAmountInsurance}
                    />
                )}
                {PSCoveragePeriod && (
                    <PolicyCKEditorTextModal
                        title='Coverage Period'
                        description='Enter the applicable coverage period wording.'
                        isEditing={isPCPCoveragePeriod}
                        onEdit={() => dispatch(setPolicyCoveragePremiumDetailsType("coveragePeriod"))}
                        onCancel={() => dispatch(setPolicyCoveragePremiumDetailsType(null))}
                        onSave={() => dispatch(setPolicyCoveragePremiumDetailsType(null))}
                        value={policyCoveragePeriod}
                    />
                )}
                <PolicySchedulePremiums isShow={PSPremiums}/>
                <PolicySchedulePremiumsRates isShow={PSPremiumRates}/>
                {PSRefundPremiums && (
                    <PolicyCKEditorTextModal
                        title='Refund of Premiums'
                        description='Enter the applicable refund of premiums wording.'
                        isEditing={isPCPRefundOfPremiums}
                        onEdit={() => dispatch(setPolicyCoveragePremiumDetailsType("refundOfPremiums"))}
                        onCancel={() => dispatch(setPolicyCoveragePremiumDetailsType(null))}
                        onSave={() => dispatch(setPolicyCoveragePremiumDetailsType(null))}
                        value={policyRefundOfPremiums}
                    />
                )}
                { isGTLIPolicy && (
                    <div className="">
                        <div className="flex items-center gap-2 rounded border border-[#c7e0f3] bg-[#eef6fc] px-2 py-2">
                            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#186cb2] text-xs text-white">
                                ✓
                            </span>
                            <span className="text-xs font-medium text-gray-700">
                                Rate Applied
                            </span>
                            <span className="text-[10px] text-gray-400">
                                The approved rate will be reflected in the policy once policy creation is completed.
                            </span>
                        </div>
                    </div>
                )}
                {(isGCLIPolicy || isGTLIPolicy) && (
                    <div className=''>
                        <div className="flex items-center gap-2 rounded border border-[#c7e0f3] bg-[#eef6fc] px-2 py-2">
                            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#186cb2] text-xs text-white">
                                ✓
                            </span>
                            <span className="text-xs font-medium text-gray-700">
                                Inclusive
                            </span>
                            <span className="text-[10px] text-gray-400">
                                Taxes are included in the premium.
                            </span>
                        </div>
                    </div>
                )}
                <PolicyScheduleTerminationAge isShow={PSTerminationAge}/>
            </div>
            <CKEditor
                isOpen={isCKEditorType(policyCoveragePremium)}
                title={isCKEditorType(policyCoveragePremium) ? getPolicyInformationTitle(policyCoveragePremium) : "" }
                value={isCKEditorType(policyCoveragePremium) ? getPolicyInformationValue( policyCoveragePremium ) : "" }
                onChange={onChangeCKEditor}
                onClose={handleCancelEditPolicySchedule}
                onSave={handleCKEditorSave}
                isLoading={isLoadingUpdatePolicy}
            />
        </section>
    )
}
